import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useChatStore,
  getChatByParticipantId,
  chatApi,
} from '@entities/messanger';
import { useWebSocketContext } from '@shared/context/WebSocketProvider';

const WAIT_CHAT_TIMEOUT = 10000;

// Ждём, пока чат с указанным id появится в сторе (придёт в HELLO)
const waitForChat = (chatId) =>
  new Promise((resolve) => {
    const findChat = () =>
      useChatStore.getState().chats.find((el) => el?.id === chatId) || null;

    const existing = findChat();
    if (existing) return resolve(existing);

    let settled = false;
    const done = (chat) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(chat);
    };

    const unsubscribe = useChatStore.subscribe(() => {
      const chat = findChat();
      if (chat) done(chat);
    });

    const timer = setTimeout(() => done(null), WAIT_CHAT_TIMEOUT);
  });

// Переход в мессенджер к чату с преподавателем.
// Если чата ещё нет — сначала создаём его через /api/chats/create.
export const useStartChat = () => {
  const navigate = useNavigate();
  const { getWebSocket } = useWebSocketContext();
  const [isCreating, setIsCreating] = useState(false);

  const startChat = useCallback(
    async (teacherId) => {
      if (!teacherId || isCreating) return;

      const store = useChatStore.getState();
      let chat = getChatByParticipantId(store.chats, teacherId);

      if (!chat) {
        setIsCreating(true);
        try {
          const res = await chatApi.createChat({ teacher_id: teacherId });
          const chatId = res?.data?.chat_id;

          if (chatId) {
            // Бэкенд возвращает только { chat_id }. Полный чат придёт в HELLO,
            // а HELLO сервер шлёт только при подключении — форсим реконнект
            // и ждём появления чата в сторе.
            getWebSocket()?.close();
            chat = await waitForChat(chatId);
          }
        } finally {
          setIsCreating(false);
        }
      }

      if (chat) {
        useChatStore.getState().selectChat(chat);
        navigate('/messenger');
      }
    },
    [isCreating, navigate, getWebSocket]
  );

  return { startChat, isCreating };
};
